import type { AuthUser } from "./types";

const encode = (value: string): string => btoa(unescape(encodeURIComponent(value)));
const decode = (value: string): string => decodeURIComponent(escape(atob(value)));

export const STORAGE_KEYS = {
  TOKEN: "maleva_auth_token",
  USER: "maleva_auth_user",
} as const;

const readStorage = (key: string): string | null =>
  localStorage.getItem(key) ?? sessionStorage.getItem(key);

const writeStorage = (key: string, value: string): void => {
  localStorage.setItem(key, value);
  sessionStorage.removeItem(key);
};

export const authStorage = {
  setAuth(userData: AuthUser): void {
    const token =
      typeof userData.token === "string" && userData.token.trim().length > 0
        ? userData.token
        : null;

    if (token) {
      writeStorage(STORAGE_KEYS.TOKEN, encode(token));
    }

    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    sessionStorage.removeItem(STORAGE_KEYS.USER);
  },

  getAuth(): AuthUser | null {
    const user = readStorage(STORAGE_KEYS.USER);
    if (!user) {
      return null;
    }

    try {
      return JSON.parse(user) as AuthUser;
    } catch {
      this.clear();
      return null;
    }
  },

  getToken(): string | null {
    const encodedToken = readStorage(STORAGE_KEYS.TOKEN);
    if (!encodedToken) {
      return null;
    }

    try {
      return decode(encodedToken);
    } catch {
      return encodedToken;
    }
  },

  updateToken(token: string): void {
    const existingUser = this.getAuth() ?? {};
    writeStorage(STORAGE_KEYS.TOKEN, encode(token));
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify({ ...existingUser, token }));
  },

  clear(): void {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.USER);
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.USER);
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");
  },

  hasToken(): boolean {
    return Boolean(readStorage(STORAGE_KEYS.TOKEN));
  },
};
